import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

// Mounts on all three subdomain/accounts/subscriptions/{plans,teams,users}/show.html.haml pages,
// which render different subsets of this DOM: only plans/show has the agreement modal, the mobile
// plan-features toggle and #total-{seats,teams}-count, and only one of #cart-seats/#cart-teams
// exists on teams/show vs users/show. Every lookup is written so an absent element is a silent
// no-op. #more-details/#plan-details render on no current view and are dead today.
//
// Everything is bound once in connect(): the ajax responses here only rewrite targeted child
// elements (tax/total labels, agreement text), never this controller's own root or its controls,
// so nothing needs re-binding afterwards.
//
// Two bindings deliberately stay on jQuery:
//   - #subscription_billing_address_id / #subscription_payment_method_id, because the billing
//     address and payment method controllers announce their swaps with a jQuery
//     `.trigger("change")`, which a native addEventListener would never see. Namespaced and
//     unbound in disconnect(), or jQuery delegation accumulates a listener per reconnect.
//   - $(agreementModal).modal("show"), a Bootstrap plugin call rather than a DOM API.
//
// R1 skip, all 12 sites: every data attribute this controller reads off `planEl` and off
// #price-per-seat/#price-per-team is read off an element other than this.element (root-sibling
// or a global id lookup), so the Values API - scoped to this.element's own attributes - cannot
// express any of them, target or not (D13).
export default class extends Controller {
  static get targets() {
    return [
      "seatCountInput", "seatCountLabel", "seatPriceLabel",
      "teamCountInput", "teamCountLabel", "teamPriceLabel",
      "form", "submitButton", "mobileActions", "featuresAndAddons", "moreDetails"
    ];
  }

  // "opened" is this controller's own mobile-features-toggle state, applied to two of its own
  // descendants (.mobile-actions/.features-and-addons) in tandem - a controller-specific class on
  // the controller's own territory, not a global utility (D1). Declared on all three mounts even
  // though the elements it targets only render on plans/show, so the declaration is never missing
  // regardless of how the two `?.`-guarded call sites below are read (D1's "sweep every emitter").
  static get classes() {
    return ["opened"];
  }

  connect() {
    this.seatCountInput = this.hasSeatCountInputTarget ? this.seatCountInputTarget : null;
    this.seatCountLabel = this.hasSeatCountLabelTarget ? this.seatCountLabelTarget : null;
    this.seatPriceLabel = this.hasSeatPriceLabelTarget ? this.seatPriceLabelTarget : null;
    this.totalSeatsCountLabel = document.getElementById("total-seats-count");

    this.teamCountInput = this.hasTeamCountInputTarget ? this.teamCountInputTarget : null;
    this.teamCountLabel = this.hasTeamCountLabelTarget ? this.teamCountLabelTarget : null;
    this.teamPriceLabel = this.hasTeamPriceLabelTarget ? this.teamPriceLabelTarget : null;
    this.totalTeamsCountLabel = document.getElementById("total-teams-count");

    // #plan is a page-level sibling of this controller's root in every mount, not a descendant,
    // so it cannot be a target - see the class-level R1 note (D13).
    const planEl = document.getElementById("plan");
    this.regenerateAgreementTextUrl = planEl?.dataset.regenerateAgreementTextUrl;
    this.taxRateUrl = planEl?.dataset.taxUrl;
    this.taxAddOnsOnly = planEl?.dataset.taxAddOnsOnly === "true";
    this.seatsIncludedCount = parseFloat(document.getElementById("price-per-seat")?.dataset.price) || 0;
    this.teamsIncludedCount = parseFloat(document.getElementById("price-per-team")?.dataset.price) || 0;
    this.pricePerSeat = parseFloat(document.getElementById("price-per-seat")?.dataset.price) || 0;
    this.pricePerTeam = parseFloat(document.getElementById("price-per-team")?.dataset.price) || 0;
    this.seatPlanPriceId = parseFloat(document.getElementById("price-per-seat")?.dataset.id);
    this.teamPlanPriceId = parseFloat(document.getElementById("price-per-team")?.dataset.id);
    this.completelyFree = planEl?.dataset.completelyFree === "true";
    this.taxExempt = planEl?.dataset.taxExempt === "true";
    this.skipAgreement = planEl?.dataset.skipAgreement === "true";

    this.formEl = this.hasFormTarget ? this.formTarget : null;
    this.submitButton = this.hasSubmitButtonTarget ? this.submitButtonTarget : null;
    this.agreementButton = this.formEl?.querySelector("#accept-agreement-button");
    this.agreementModal = this.formEl?.querySelector("#subscription-terms");
    this.agreementTextEl = this.agreementModal?.querySelector(".subscription-agreement");
    this.signature = this.formEl?.querySelector("#subscription_signature");
    this.mobileActions = this.hasMobileActionsTarget ? this.mobileActionsTarget : null;
    this.featuresAndAddons = this.hasFeaturesAndAddonsTarget ? this.featuresAndAddonsTarget : null;
    this.cartTaxLabel = document.getElementById("cart-tax")?.querySelector("td");
    this.cartTotalLabel = document.getElementById("cart-total")?.querySelector("td");
    this.taxAmountCentsInput = document.getElementById("subscription_tax_amount_cents");

    this.formatter = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

    // #more-details/#plan-details are dead on every current view (class comment) - kept as
    // targets/an R4 skip rather than deleted, since removing dead code isn't this pass's job.
    this.moreDetailsEl = this.hasMoreDetailsTarget ? this.moreDetailsTarget : null;
    this.moreDetailsEl?.addEventListener("click", (e) => {
      e.preventDefault();
      // R4 skip: #plan-details is never this controller's own mount (D14), and "hidden" is the
      // app-wide utility class (D1) even where it is.
      document.getElementById("plan-details")?.classList.toggle("hidden");
    });

    this.seatCountInput?.addEventListener("change", () => { this.updatePurchaseDetails(); this.updateAgreementDetails(); });
    this.seatCountInput?.addEventListener("keyup", () => { this.updatePurchaseDetails(); this.updateAgreementDetails(); });

    this.teamCountInput?.addEventListener("change", () => { this.updatePurchaseDetails(); this.updateAgreementDetails(); });
    this.teamCountInput?.addEventListener("keyup", () => { this.updatePurchaseDetails(); this.updateAgreementDetails(); });

    this.submitButton?.addEventListener("click", (e) => {
      e.preventDefault();
      if (this.skipAgreement) {
        this.submitPurchase();
      } else {
        $(this.agreementModal).modal("show");
      }
    });

    this.agreementButton?.addEventListener("click", (e) => {
      e.preventDefault();
      this.agreementButton.disabled = true;
      this.submitPurchase();
    });

    // Real native DOM event, scoped to this controller's own (never-replaced) signature input -
    // a jQuery body-delegation was never load-bearing here the way it is for the two
    // cross-controller listeners above (see the class-level comment).
    this.signature?.addEventListener("keyup", () => this.updateAgreementButton());

    this.onBillingAddressChange = () => { this.updatePurchaseDetails(); this.updateSubmitButton(); };
    this.onPaymentMethodChange = () => { this.updateSubmitButton(); };
    $("body").on("change.subscriptionPurchases", "#subscription_billing_address_id", this.onBillingAddressChange);
    $("body").on("change.subscriptionPurchases", "#subscription_payment_method_id", this.onPaymentMethodChange);

    this.updatePurchaseDetails();
    this.updateSubmitButton();
  }

  disconnect() {
    $("body").off("change.subscriptionPurchases", "#subscription_billing_address_id", this.onBillingAddressChange);
    $("body").off("change.subscriptionPurchases", "#subscription_payment_method_id", this.onPaymentMethodChange);
  }

  toggleMobilePlanFeatures(event) {
    event.preventDefault();
    this.mobileActions?.classList.toggle(this.openedClass);
    this.featuresAndAddons?.classList.toggle(this.openedClass);
  }

  // R3: wired via data-action in the markup rather than a manual addEventListener, now that R2
  // no longer needs a querySelector/Target to find #increase-seats et al just to attach one.
  increaseSeats() {
    this.seatCountInput.value = this.seatsCount() + 1;
    this.updatePurchaseDetails();
    this.updateAgreementDetails();
  }

  decreaseSeats() {
    const current = this.seatsCount();
    if (current > 0) {
      this.seatCountInput.value = current - 1;
      this.updatePurchaseDetails();
      this.updateAgreementDetails();
    }
  }

  increaseTeams() {
    this.teamCountInput.value = this.teamsCount() + 1;
    this.updatePurchaseDetails();
    this.updateAgreementDetails();
  }

  decreaseTeams() {
    const current = this.teamsCount();
    if (current > 0) {
      this.teamCountInput.value = current - 1;
      this.updatePurchaseDetails();
      this.updateAgreementDetails();
    }
  }

  seatsCount() {
    return parseInt(this.seatCountInput?.value) || 0;
  }

  teamsCount() {
    return parseInt(this.teamCountInput?.value) || 0;
  }

  // jQuery's .val() on a selection matching nothing returns `undefined`, not "", and
  // `undefined !== ""`. #subscription_tax_amount_cents never renders on plans/show, so the
  // hasTaxAmount check is vacuously true there. Long-standing; this is pre-submit gating only.
  jqVal(el) {
    return el ? el.value : undefined;
  }

  submittable() {
    if (this.completelyFree) { return true; }

    const hasPaymentMethod = this.jqVal(this.formEl?.querySelector("#subscription_payment_method_id")) !== "";
    const hasBillingAddress = this.jqVal(this.formEl?.querySelector("#subscription_billing_address_id")) !== "";
    const hasTaxAmount = this.taxExempt || this.jqVal(this.taxAmountCentsInput) !== "";

    let validAddOns = true;

    if (!document.getElementById("plan-details-wrapper")) {
      const teamInput = document.querySelector("input[name=team_add_on_quantity]");
      if (teamInput && parseInt(teamInput.value) < 1) { validAddOns = false; }

      const userInput = document.querySelector("input[name=user_add_on_quantity]");
      if (userInput && parseInt(userInput.value) < 1) { validAddOns = false; }
    }

    return hasPaymentMethod && hasBillingAddress && hasTaxAmount && validAddOns;
  }

  updateSubmitButton() {
    if (this.submitButton) { this.submitButton.disabled = !this.submittable(); }
  }

  updateAgreementButton() {
    if (this.agreementButton) {
      this.agreementButton.disabled = !(this.submittable() && (this.signature?.value || "") !== "");
    }
  }

  async updatePurchaseDetails() {
    if (this.taxAmountCentsInput) { this.taxAmountCentsInput.value = ""; }
    this.updateSubmitButton();
    if (this.cartTaxLabel) { this.cartTaxLabel.innerHTML = "Calculating..."; }

    const seatsPrice = this.seatsCount() * this.pricePerSeat;
    const teamsPrice = this.teamsCount() * this.pricePerTeam;

    if (this.totalSeatsCountLabel) { this.totalSeatsCountLabel.textContent = this.seatsCount() + this.seatsIncludedCount; }
    if (this.totalTeamsCountLabel) { this.totalTeamsCountLabel.textContent = this.teamsCount() + this.teamsIncludedCount; }
    if (this.seatCountLabel) { this.seatCountLabel.textContent = this.seatsCount(); }
    if (this.teamCountLabel) { this.teamCountLabel.textContent = this.teamsCount(); }

    if (!this.taxRateUrl) { return; }

    const params = {
      user: { plan_price_id: this.seatPlanPriceId, quantity: this.seatsCount() },
      team: { plan_price_id: this.teamPlanPriceId, quantity: this.teamsCount() },
      tax_add_ons_only: this.taxAddOnsOnly
    };

    let response;
    try {
      response = await this.postJSON(this.taxRateUrl, params);
    } catch (e) {
      console.warn(e);
      return;
    }

    const data = await response.json();
    if (this.seatPriceLabel) { this.seatPriceLabel.textContent = this.formatter.format(seatsPrice); }
    if (this.teamPriceLabel) { this.teamPriceLabel.textContent = this.formatter.format(teamsPrice); }

    if (data.missing === undefined) {
      if (this.cartTaxLabel) { this.cartTaxLabel.textContent = this.formatter.format(parseFloat(data.tax_amount)); }
      if (this.cartTotalLabel) { this.cartTotalLabel.textContent = this.formatter.format(parseFloat(data.total)); }
      if (this.taxAmountCentsInput) { this.taxAmountCentsInput.value = parseInt(data.tax_amount * 100); }
      this.updateSubmitButton();
    } else {
      if (this.cartTaxLabel) { this.cartTaxLabel.textContent = data.missing; }
      if (this.taxAmountCentsInput) { this.taxAmountCentsInput.value = ""; }
      this.updateSubmitButton();
    }
  }

  async updateAgreementDetails() {
    // #plan only carries this URL on plans/show; teams/users show are skip_agreement pages with
    // no modal to regenerate text for.
    if (!this.regenerateAgreementTextUrl) { return; }

    const params = {
      user: { plan_price_id: this.seatPlanPriceId, quantity: this.seatsCount() },
      team: { plan_price_id: this.teamPlanPriceId, quantity: this.teamsCount() }
    };

    let response;
    try {
      response = await this.postJSON(this.regenerateAgreementTextUrl, params);
    } catch (e) {
      console.warn(e);
      return;
    }

    const data = await response.json();
    // S1: server-rendered HTML from our own origin - Subdomain::Accounts::Subscriptions::
    // PlansController#regenerate_agreement_text renders SubscriptionAgreementGenerator#
    // body_tag_v2, the same string plans#show's own `= @agreement_text` renders unescaped on
    // first load. body_tag_v2's interpolated fields (customer/org/plan name) are now
    // HTML-escaped at the source (see subscription_agreement_generator.rb) rather than trusted
    // by name the way CampaignDecorator#sanitized_name turned out not to be - do not revert that
    // escaping without re-auditing this call site.
    if (this.agreementTextEl) { this.agreementTextEl.innerHTML = data.agreement_text; }
  }

  async submitPurchase() {
    if (this.submitButton) { this.submitButton.disabled = true; }
    // R4 skip: "hidden" is the app-wide utility class (D1), not a name this controller owns.
    this.formEl?.querySelector(".loader")?.classList.remove("hidden");

    const params = {
      subscription: {
        plan_price_id: this.formEl?.querySelector("#subscription_plan_price_id")?.value,
        payment_method_id: this.formEl?.querySelector("#subscription_payment_method_id")?.value,
        billing_address_id: this.formEl?.querySelector("#subscription_billing_address_id")?.value,
        user_add_on_quantity: this.seatsCount(),
        team_add_on_quantity: this.teamsCount(),
        user_add_on_price_id: this.seatPlanPriceId,
        team_add_on_price_id: this.teamPlanPriceId,
        tax_amount_cents: this.taxExempt ? 0 : this.taxAmountCentsInput?.value,
        signature: this.signature?.value
      }
    };

    const options = RequestHelper.defaultPostOptions();
    options.headers = Object.assign({}, options.headers, {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "Accept": "application/json, text/plain, */*"
    });
    options.body = JSON.stringify(params);

    let response;
    try {
      response = await fetch(this.formEl.action, options);
    } catch (e) {
      console.warn(e);
      this.onPurchaseFailure({ message: "Something went wrong. Please try again." });
      return;
    }

    const data = await response.json();

    if (response.ok) {
      if (data && data.ga4_events) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(...JSON.parse(data.ga4_events));
      }

      // S3: our own controller's JSON response, same origin as this page - asserted before
      // navigating per the S3 rule rather than trusting `data.redirect_url` outright. The
      // failing branch does not navigate.
      if (RequestHelper.isSameOriginUrl(data.redirect_url)) {
        window.location.href = data.redirect_url;
      } else {
        this.onPurchaseFailure({ message: "Something went wrong. Please try again." });
      }
      return;
    }

    this.onPurchaseFailure(data);
  }

  onPurchaseFailure(data) {
    // R4 skip: "hidden" is the app-wide utility class (D1), not a name this controller owns.
    this.formEl?.querySelector(".loader")?.classList.add("hidden");
    if (this.agreementButton) { this.agreementButton.disabled = false; }
    if (this.submitButton) { this.submitButton.disabled = false; }
    window.ltAlert({ message: data.message, type: "toast-error" });
  }

  async postJSON(url, params) {
    const options = RequestHelper.defaultPostOptions();
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" });
    options.body = JSON.stringify(params);
    return fetch(url, options);
  }
}
